import {
  Footer,
  FooterNewsletter,
  FooterColumn,
  FooterLink,
  FooterCredits,
} from "@/components/layout/footer";
import { useNewsletterSubscription } from "@/hooks/use-newsletter-subscription";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useClientTranslation } from "@/hooks/use-client-translation";

/* -------------------------------------------------------------------------- */
/*                                   Logo                                     */
/* -------------------------------------------------------------------------- */

function Logo() {
  return (
    <Link to="/" className="flex items-center">
      <img 
        src="/mezepay-logo1.svg" 
        alt="MezePay" 
        className="h-11 brightness-0 invert"
      />
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Social Icons                                  */
/* -------------------------------------------------------------------------- */

const SocialIcon = ({ src, alt }: { src: string; alt: string }) => (
  <img src={src} alt={alt} className="w-6 h-6 brightness-0 invert" />
);

/* -------------------------------------------------------------------------- */
/*                              Site Footer                                   */
/* -------------------------------------------------------------------------- */

export function SiteFooter() {
  const { t } = useClientTranslation();
  const { subscribe, isLoading, isSuccess } =
    useNewsletterSubscription();

  const handleSubscribe = async (
    email: string
  ) => {
    const result = await subscribe(email);
    if (result.success) {
      toast.success(
        t("footer.newsletter.success")
      );
    } else {
      toast.error(
        result.error ||
          t("footer.newsletter.error")
      );
    }
  };

  return (
    <Footer
      id="contact"
      className="bg-purple-primary text-white"
      newsletter={
        <FooterNewsletter
          logo={<Logo />}
          description={t("footer.newsletter.description")}
          placeholder={t("footer.newsletter.placeholder")}
          buttonText={
            isLoading
              ? t("footer.newsletter.subscribing")
              : isSuccess
              ? t("footer.newsletter.subscribed")
              : t("footer.newsletter.subscribe")
          }
          isLoading={isLoading}
          privacyNotice={
            <span className="text-white text-xs">
              {t("footer.newsletter.privacy")}
            </span>
          }
          onSubmit={handleSubscribe}
        />
      }
      columns={
        <>
          <FooterColumn title={t("footer.columns.product")}>
            <FooterLink href="/">
              {t("footer.links.howItWorks")}
            </FooterLink>
            <FooterLink href="/for-restaurants">
              {t("footer.links.forRestaurants")}
            </FooterLink>
            <FooterLink href="/blog">
              {t("nav.blog")}
            </FooterLink>
          </FooterColumn>
          <FooterColumn title={t("footer.columns.company")}>
            <FooterLink href="/">
              {t("footer.links.about")}
            </FooterLink>
            <FooterLink href="mailto:Info@mezepay.com">
              {t("footer.links.contact")}
            </FooterLink>
            <FooterLink href="/terms-of-service">
              {t("footer.links.terms")}
            </FooterLink>
            <FooterLink href="/privacy-policy">
              {t("footer.links.privacy")}
            </FooterLink>
          </FooterColumn>
          <FooterColumn title={t("footer.columns.followUs")}>
            <FooterLink
              href="https://www.facebook.com/share/1CKUrHma63/?mibextid=wwXIfr"
              external
              icon={
                <SocialIcon src="/images/facebook.svg" alt="Facebook" />
              }
            >
              Facebook
            </FooterLink>
            <FooterLink
              href="https://www.instagram.com/mezepay?igsh=MTh2Z3J1OTN5aXEzOQ%3D%3D&utm_source=qr"
              external
              icon={
                <SocialIcon src="/images/instagram.svg" alt="Instagram" />
              }
            >
              Instagram
            </FooterLink>
            <FooterLink
              href="https://x.com/mezepay?s=21"
              external
              icon={
                <SocialIcon src="/images/twitter.svg" alt="X" />
              }
            >
              X
            </FooterLink>
            <FooterLink
              href="https://www.linkedin.com/company/mezepay/"
              external
              icon={
                <SocialIcon src="/images/linkedin.svg" alt="LinkedIn" />
              }
            >
              LinkedIn
            </FooterLink>
          </FooterColumn>
        </>
      }
      credits={
        <FooterCredits
          copyright={
            <span>
              {t("footer.copyright")}
            </span>
          }
        />
      }
    />
  );
}
